import { useMemo, useRef, useState } from 'react'
import { MAX_REPLY_CHARS, MAX_TICKET_CHARS, PRIORITY_LABELS, STATUS_LABELS, parseNotes, replyChecks, replyScore } from '../../shared/support.js'
import { Icon } from '../components/icons/Icon.jsx'
import { SupportSidebar } from '../components/sidebar/SupportSidebar.jsx'
import { LoadingState } from '../components/ui/LoadingState.jsx'
import { copyText } from '../lib/clipboard.js'
import {
  useAnalyzeTicket,
  useDeleteTicket,
  useDraftReply,
  useImproveReply,
  useRewriteReply,
  useSaveTicket,
  useSupportTickets,
} from '../lib/queries/support.js'
import p from './pages.module.css'
import s from './support.module.css'

const TONE = { crit: s.tagCrit, warn: s.tagWarn, info: s.tagInfo, muted: s.tagMuted }
const PRIORITY_TONE = { critical: s.tagCrit, high: s.tagWarn, medium: s.tagInfo, low: s.tagMuted }

/** Support: paste a ticket, see what we have and what is missing, draft and save the reply. */
export function SupportPage() {
  const tickets = useSupportTickets()
  const analyze = useAnalyzeTicket()
  const draft = useDraftReply()
  const improve = useImproveReply()
  const rewrite = useRewriteReply()
  const save = useSaveTicket()
  const remove = useDeleteTicket()

  const [ticket, setTicket] = useState('')
  const [analysis, setAnalysis] = useState(null)
  const [reply, setReply] = useState('')
  const [notes, setNotes] = useState('')
  const [ticketOpen, setTicketOpen] = useState(true)
  const [savedId, setSavedId] = useState(null)
  const [dirty, setDirty] = useState(false)
  const [previous, setPrevious] = useState(null)
  const [changes, setChanges] = useState(null)
  const [copied, setCopied] = useState(false)
  const ticketRef = useRef(null)

  const checks = useMemo(() => replyChecks(reply, analysis), [reply, analysis])
  const score = useMemo(() => replyScore(reply, analysis), [reply, analysis])
  const words = reply.trim() ? reply.trim().split(/\s+/).length : 0
  const busy = analyze.isPending || draft.isPending || improve.isPending || rewrite.isPending

  function reset() {
    setTicket('')
    setAnalysis(null)
    setReply('')
    setNotes('')
    setTicketOpen(true)
    setSavedId(null)
    setDirty(false)
    setPrevious(null)
    setChanges(null)
    ticketRef.current?.focus()
  }

  function open(saved) {
    setTicket(saved.ticket)
    setAnalysis(saved.analysis)
    setReply(saved.reply)
    setNotes(saved.notes ?? '')
    setTicketOpen(false)
    setSavedId(saved.id)
    setDirty(false)
    setPrevious(null)
    setChanges(null)
  }

  function runAnalyze() {
    if (!ticket.trim()) return ticketRef.current?.focus()
    analyze.mutate(ticket, {
      onSuccess: (data) => {
        setAnalysis(data.analysis)
        setTicketOpen(false)
        setSavedId(null)
        setDirty(true)
      },
    })
  }

  function runDraft() {
    draft.mutate(
      { ticket, analysis },
      {
        onSuccess: (out) => {
          setReply(out.reply)
          setNotes(out.internalNotes)
          setPrevious(null)
          setChanges(null)
          setDirty(true)
        },
      },
    )
  }

  function runRewrite() {
    // Works with or without a ticket; the analysis is sent only when it matches the pasted ticket.
    const hasTicket = ticket.trim().length >= 10
    rewrite.mutate(
      { reply, ...(hasTicket ? { ticket, ...(analysis ? { analysis } : {}) } : {}) },
      {
        onSuccess: (out) => {
          setPrevious(reply)
          setReply(out.reply)
          setChanges({ items: out.changes ?? [], noTicket: !hasTicket })
          setDirty(true)
        },
      },
    )
  }

  function runImprove() {
    improve.mutate(reply, {
      onSuccess: (text) => {
        setPrevious(reply)
        setChanges(null)
        setReply(text)
        setDirty(true)
      },
    })
  }

  function runSave() {
    save.mutate(
      { id: savedId ?? undefined, ticket, analysis, reply, notes },
      {
        onSuccess: (saved) => {
          setSavedId(saved.id)
          setDirty(false)
        },
      },
    )
  }

  async function copy() {
    if (await copyText(reply)) {
      setCopied(true)
      setTimeout(() => setCopied(false), 1400)
    }
  }

  const steps = [
    ['Paste', ticket.trim() ? 'done' : 'on'],
    ['Understand', analysis ? 'done' : ticket.trim() ? 'on' : ''],
    ['Reply', reply.trim() ? 'done' : analysis ? 'on' : ''],
    ['Save', savedId && !dirty ? 'done' : reply.trim() ? 'on' : ''],
  ]
  const placeholders = [...new Set(reply.match(/\[(day|name)\]/g) ?? [])]
  const status = savedId ? (dirty ? 'Saved ticket has unsaved changes.' : 'Saved to history.') : analysis || reply.trim() ? 'Not saved. Leaving this ticket discards the analysis.' : ''

  return (
    <div className={p.shell}>
      <SupportSidebar
        tickets={tickets}
        activeId={savedId}
        onNew={reset}
        onOpen={open}
        onDelete={(t) => {
          remove.mutate(t.id)
          if (t.id === savedId) reset()
        }}
      />

      <main className={p.main}>
        <div className={s.canvas}>
          <div className={s.canvasIn}>
            <div className={s.head}>
              <div>
                <h1 className={s.title}>{analysis?.title ?? 'New ticket'}</h1>
                <p className={s.sub}>
                  {analysis ? [analysis.customer, savedId ? 'saved' : 'not saved'].filter(Boolean).join(' · ') : 'Paste the ticket. Understand it. Send a reply you trust.'}
                </p>
              </div>
              <ol className={s.steps} aria-label="Progress">
                {steps.map(([name, state], i) => (
                  <li key={name} className={`${s.step} ${state === 'on' ? s.stepOn : ''} ${state === 'done' ? s.stepDone : ''}`}>
                    <i>{state === 'done' ? <Icon name="check" size={11} strokeWidth={2} /> : i + 1}</i>
                    {name}
                  </li>
                ))}
              </ol>
            </div>

            <section className={s.panel}>
              <h2 className={s.h}>
                1 · Ticket
                <span className={s.hActions}>
                  {analysis && !ticketOpen ? (
                    <button type="button" className={s.ghost} onClick={() => setTicketOpen(true)}>
                      Edit
                    </button>
                  ) : null}
                  <button type="button" className={s.ghost} onClick={reset}>
                    Clear
                  </button>
                </span>
              </h2>
              {analysis && !ticketOpen ? (
                <p className={s.ticketPeek}>{ticket}</p>
              ) : (
                <>
                  <textarea
                    ref={ticketRef}
                    className={s.big}
                    value={ticket}
                    maxLength={MAX_TICKET_CHARS}
                    onChange={(e) => setTicket(e.target.value)}
                    placeholder="Paste the full conversation here, including the customer's latest message and any log lines."
                    aria-label="Ticket conversation"
                  />
                  <div className={s.panelFoot}>
                    <span className={s.hint}>{ticket.length.toLocaleString()} / {MAX_TICKET_CHARS.toLocaleString()} characters</span>
                    <button type="button" className={s.primary} onClick={runAnalyze} disabled={busy || ticket.trim().length < 10}>
                      {analyze.isPending ? 'Analyzing…' : analysis ? 'Analyze again' : 'Analyze ticket'}
                      <Icon name="arrow-right" size={14} />
                    </button>
                  </div>
                </>
              )}
            </section>

            {analyze.isPending || analysis ? (
              <section className={s.panel} aria-live="polite">
                <h2 className={s.h}>2 · Analysis</h2>
                {analyze.isPending ? (
                  <LoadingState label="Reading the ticket" />
                ) : (
                  <div>
                    <p className={s.summary}>{analysis.summary}</p>
                    <div className={s.tags}>
                      <span className={`${s.tag} ${PRIORITY_TONE[analysis.priority] ?? s.tagInfo}`} title={analysis.priorityReason || undefined}>
                        {PRIORITY_LABELS[analysis.priority] ?? 'Medium'} priority
                      </span>
                      <label className={s.statusPick}>
                        Status
                        <select
                          value={analysis.status ?? 'open'}
                          onChange={(e) => {
                            setAnalysis({ ...analysis, status: e.target.value })
                            setDirty(true)
                          }}
                          aria-label="Ticket status, used to shape the reply"
                        >
                          {Object.entries(STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    {analysis.nextAction ? (
                      <p className={s.hint}>
                        <b>Next action:</b> {analysis.nextAction}
                      </p>
                    ) : null}
                    {analysis.tags.length ? (
                      <div className={s.tags}>
                        {analysis.tags.slice(0, 4).map((t) => (
                          <span key={t.label} className={`${s.tag} ${TONE[t.tone]}`}>
                            {t.label}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {analysis.flags?.length ? (
                      <div className={s.flags} role="note">
                        <b>Check before replying</b>
                        <ul>
                          {analysis.flags.map((f) => (
                            <li key={f}>{f}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    <div className={s.facts}>
                      <Fact label="They gave" tone="have" items={analysis.have} empty="Nothing concrete yet" />
                      <Fact label="Still need" tone="need" items={analysis.need} empty="Nothing, we have enough" />
                      <Fact label="We check" items={analysis.ourChecks} />
                      <Fact
                        label="Likely cause"
                        items={(analysis.hypotheses ?? []).slice(0, 2).map((hyp) => `${hyp.cause} (${hyp.fit})`)}
                        note="Not confirmed"
                      />
                    </div>
                  </div>
                )}
              </section>
            ) : null}


            <section className={s.panel}>
              <h2 className={s.h}>
                3 · Your reply <span className={`${s.tag} ${s.tagMuted}`}>Human review required</span>
              </h2>
              <div className={s.reply}>
                <div className={s.replyMain}>
                  <div className={s.bar}>
                    <button type="button" className={s.primary} onClick={runDraft} disabled={busy || !analysis}>
                      {draft.isPending ? 'Drafting…' : 'Draft reply'}
                    </button>
                    <button type="button" className={s.rewrite} onClick={runRewrite} disabled={busy || !reply.trim()}>
                      {rewrite.isPending ? 'Rewriting…' : 'Rewrite my draft'}
                    </button>
                    {!analysis && !reply.trim() ? <span className={s.hint}>Analyze the ticket to draft, or write your own and rewrite it.</span> : null}
                    <span className={s.grow} />
                    <button type="button" className={s.ghost} onClick={() => { setReply(''); setNotes(''); setPrevious(null); setChanges(null) }} disabled={!reply}>
                      Clear
                    </button>
                  </div>
                  <textarea
                    className={`${s.big} ${s.replyBox}`}
                    value={reply}
                    maxLength={MAX_REPLY_CHARS}
                    onChange={(e) => {
                      setReply(e.target.value)
                      setDirty(true)
                    }}
                    placeholder="Write your own reply, or draft one. Improve fixes the English without changing any technical detail."
                    aria-label="Reply"
                  />
                  {placeholders.length ? (
                    <p className={s.flags} role="note">
                      Fill in {placeholders.join(' and ')} before sending.
                    </p>
                  ) : null}
                  {status ? <p className={s.hint}>{status}</p> : null}
                  {changes ? (
                    <div className={s.changed}>
                      <b>What changed</b>
                      {changes.items.length ? (
                        <ul>
                          {changes.items.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      ) : null}
                      {changes.noTicket ? (
                        <p>No ticket pasted, so the rewrite worked from your draft alone. Paste and analyze the ticket, then rewrite again to add its details.</p>
                      ) : null}
                    </div>
                  ) : null}
                  <aside className={s.review} aria-label="Reply review">
                    <div className={s.score}>
                      <Ring value={score} />
                      <span>
                        Score <span className={s.scoreN}>{score}</span>
                      </span>
                      <span className={s.sep}>·</span>
                      <span>{!reply.trim() ? 'Write a reply to see its score' : score >= 80 ? 'Ready for a human review' : score >= 50 ? 'Getting there' : 'Needs more detail'}</span>
                    </div>
                    <ul className={s.checks} aria-label="Reply checks">
                      {checks.map((c) => (
                        <li key={c.id} data-ok={c.ok && reply.trim() ? 'true' : 'false'}>
                          <span className={s.dot} />
                          {c.label}
                        </li>
                      ))}
                    </ul>
                  </aside>
                  <div className={s.bar}>
                    <button type="button" className={s.btn} onClick={runImprove} disabled={busy || !reply.trim()}>
                      {improve.isPending ? 'Improving…' : 'Improve English'}
                    </button>
                    {previous !== null ? (
                      <button type="button" className={s.btn} onClick={() => { setReply(previous); setPrevious(null); setChanges(null) }}>
                        Undo
                      </button>
                    ) : null}
                    <span className={`${s.hint} ${s.grow}`}>
                      {words} word{words === 1 ? '' : 's'}
                    </span>
                    <button type="button" className={s.btn} onClick={copy} disabled={!reply.trim()}>
                      <Icon name="copy" size={14} />
                      {copied ? 'Copied' : 'Copy reply'}
                    </button>
                    <button type="button" className={s.btn} onClick={runSave} disabled={save.isPending || !analysis || !ticket.trim()}>
                      <Icon name="save" size={14} />
                      {save.isPending ? 'Saving…' : 'Save to support KB'}
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {notes ? (
              <details className={`${s.panel} ${s.notes}`}>
                <summary>
                  Internal notes <span className={`${s.tag} ${s.tagCrit}`}>Do not send</span>
                </summary>
                <div className={s.notesBody}>
                  {parseNotes(notes).map((section, i) => (
                    <div key={i} className={s.notesSection}>
                      {section.title ? <h4>{section.title}</h4> : null}
                      <ul>
                        {section.items.map((item, j) => (
                          <li key={j}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </details>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  )
}

function Fact({ label, tone, items, empty, note }) {
  const list = items ?? []
  if (!list.length && !empty) return null
  return (
    <div className={s.fact}>
      <div className={`${s.factLabel} ${tone === 'have' ? s.have : tone === 'need' ? s.need : ''}`}>
        {label}
        {note ? <span className={s.factNote}>{note}</span> : null}
      </div>
      {list.length ? (
        <ul className={s.factList}>
          {list.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className={s.hint}>{empty}</p>
      )}
    </div>
  )
}

function Ring({ value }) {
  return (
    <svg className={s.ring} viewBox="0 0 80 80" aria-hidden="true">
      <circle cx="40" cy="40" r="32" fill="none" stroke="var(--line)" strokeWidth="7" />
      <circle
        cx="40"
        cy="40"
        r="32"
        fill="none"
        stroke="var(--teal)"
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray="201"
        strokeDashoffset={201 - (201 * value) / 100}
        transform="rotate(-90 40 40)"
      />
    </svg>
  )
}
